/**
 * Client-side bearer-token middleware for server functions.
 *
 * Intentionally replaces the generated `attachSupabaseAuth` middleware: that
 * one statically imports the Supabase browser SDK, which pulled ~100KB of JS
 * into the entry chunk that every public visitor downloads. Here we read the
 * already-persisted session from localStorage and only load the SDK when the
 * token is missing/near expiry and an actual refresh is required.
 */
import { createMiddleware } from "@tanstack/react-start";

const REFRESH_SKEW_SECONDS = 60;

function readStoredToken(): string | null {
  if (typeof window === "undefined") return null;
  try {
    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i);
      if (!key || !key.startsWith("sb-") || !key.endsWith("-auth-token")) continue;
      const raw = window.localStorage.getItem(key);
      if (!raw) continue;
      const parsed = JSON.parse(raw) as {
        access_token?: string;
        expires_at?: number;
      };
      if (!parsed?.access_token) continue;
      const expiresAt = typeof parsed.expires_at === "number" ? parsed.expires_at : 0;
      if (expiresAt && expiresAt - REFRESH_SKEW_SECONDS <= Date.now() / 1000) return null;
      return parsed.access_token;
    }
  } catch {
    return null;
  }
  return null;
}

function hasStoredSessionKey(): boolean {
  if (typeof window === "undefined") return false;
  try {
    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i);
      if (key?.startsWith("sb-") && key.endsWith("-auth-token")) return true;
    }
  } catch {
    return false;
  }
  return false;
}

async function getAccessToken(): Promise<string | undefined> {
  const stored = readStoredToken();
  if (stored) return stored;
  // No usable token cached. Only pay for the SDK when a session actually exists
  // (i.e. a signed-in user whose token needs refreshing).
  if (!hasStoredSessionKey()) return undefined;
  const { supabase } = await import("@/integrations/supabase/client");
  const { data } = await supabase.auth.getSession();
  return data.session?.access_token;
}

export const attachSupabaseAuthLite = createMiddleware({ type: "function" }).client(
  async ({ next }) => {
    const token = await getAccessToken();
    return next({ headers: token ? { Authorization: `Bearer ${token}` } : {} });
  },
);
